import { useState } from "react";
import HeroBusiness from "@/components/common/HeroBusiness";
import PageTitleBox from "@/components/common/PageTitleBox";
import Container from "@/components/layout/Container";
import { Link } from "react-router-dom";
import { Badge } from "@/components/common";

const BusinessMessaging = () => {
  const [activeTab, setActiveTab] = useState("sms");

  const tabs = [
    { id: "sms", label: "SMS MT/MO" },
    { id: "mms", label: "MMS MT/MO" },
    { id: "sfa", label: "SFA FFA" },
  ];

  // Step Card 컴포넌트
  const StepCard = ({
    icon,
    name,
    small = false,
  }: {
    icon: string;
    name: string;
    small?: boolean;
  }) => (
    <div
      className={`${small ? "w-[120px]" : "w-[160px] min-w-[140px]"} bg-gray-1 rounded-lg overflow-hidden shadow-[0_4px_12px_rgba(0,0,0,0.08)] transition-transform duration-300 hover:-translate-y-[5px]`}
    >
      <div
        className={`${small ? "h-[90px]" : "h-[120px]"} flex items-center justify-center p-[10px]`}
      >
        <img
          src={icon}
          alt={name}
          className={`${small ? "w-[70px]" : "w-[80px]"} max-w-full h-auto`}
        />
      </div>
      <div
        className={`bg-[#cdcdcd] ${small ? "py-[8px] px-[2px] text-[1.4rem]" : "py-[12px] px-[5px] text-[1.8rem]"} text-center`}
      >
        <span dangerouslySetInnerHTML={{ __html: name }} />
      </div>
    </div>
  );

  // Connector 컴포넌트
  const Connector = () => (
    <div className="flex items-center justify-center flex-shrink-0">
      <img src="/img/business/SyncAlt.svg" alt="" className="w-[45px] h-auto opacity-70" />
    </div>
  );

  // Flow Arrow 컴포넌트
  const FlowArrow = ({ label }: { label?: string }) => (
    <div className="text-navy flex-shrink-0 flex flex-col items-center gap-[8px]">
      {label && (
        <span className="text-[14px] font-semibold text-gray-7 text-center whitespace-pre-line">
          {label}
        </span>
      )}
      <span className="material-icons-round text-[40px]">east</span>
    </div>
  );

  return (
    <Container>
      <HeroBusiness title="비즈니스를 완성하는" />

      <section id="main-content" className="py-[70px]">
        <PageTitleBox
          badge="보유기술 및 특허"
          title={
            <>
              최고의 전문성을
              <br />
              보장합니다.
            </>
          }
          subtitle={
            <>
              국내 최대 규모의 인프라와 이중화 전송망으로,
              <br />
              끊김 없는 최적의 메시징 서비스를 보장합니다.
            </>
          }
        />

        {/* Mobile Tabs - 레거시 스타일 */}
        <nav className="mb-[60px]" aria-label="메시징 서비스 탭">
          <ul
            className="flex justify-center gap-8 p-0 m-0 list-none border-b border-gray-2"
            role="tablist"
          >
            {tabs.map(tab => (
              <li key={tab.id} className="relative">
                <button
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`bg-transparent border-none py-[var(--space-4)] min-h-[44px] inline-flex items-center text-[1.8rem] cursor-pointer transition-colors duration-300 ${
                    activeTab === tab.id ? "text-black font-bold" : "text-gray-5 hover:text-black"
                  }`}
                >
                  {tab.label}
                </button>
                <div
                  className={`absolute bottom-[-1px] left-0 h-[3px] bg-black transition-all duration-300 ${
                    activeTab === tab.id ? "w-full" : "w-0"
                  }`}
                />
              </li>
            ))}
          </ul>
        </nav>

        {/* SMS Tab Content */}
        <div className={activeTab === "sms" ? "block" : "hidden"} role="tabpanel">
          {/* SMS MT 서비스 */}
          <section className="mb-[80px]">
            <div className="mb-[50px]">
              <h2 className="text-[3.6rem] text-black font-bold mb-[25px] pb-[5px]">
                SMS MT 서비스
              </h2>
              <p className="text-[2rem]">
                기업의 서버에서 사용자(휴대폰)에게 대량의 문자 메시지를 전송하는 서비스입니다.
                <br />
                광고, 홍보, 알림 등 기업의 필수 커뮤니케이션을 위한 핵심 솔루션입니다.
              </p>
            </div>

            <Badge size="md" color="white" className="bg-[#1972D2]">
              SMS MO와 SMS MT의 개념
            </Badge>

            {/* Concept Diagram */}
            <div className="bg-[#f8f9fa] p-[40px_20px] rounded-lg text-center overflow-x-auto">
              <div className="max-w-[900px] mx-auto py-[30px]">
                {/* Guide Labels */}
                <div className="flex items-center justify-start mb-[55px] ml-[10px]">
                  <img
                    src="/img/business/SyncAlt.svg"
                    alt="SMS MT/MO Flow Guide"
                    className="w-[60px] h-auto"
                  />
                  <div className="ml-[20px] flex flex-col gap-[5px] text-left text-[1.8rem]">
                    <span className="text-navy">
                      <strong>SMS MT</strong> (Mobile Terminated)
                    </span>
                    <span className="text-red-500">
                      <strong>SMS MO</strong> (Mobile Originated)
                    </span>
                  </div>
                </div>

                {/* Process Map */}
                <div className="flex items-center justify-between gap-[10px]">
                  <StepCard icon="/img/business/icon_customer.svg" name="고객사" />
                  <Connector />
                  <StepCard icon="/img/business/icon_database.svg" name="Messaging Hub" />
                  <Connector />
                  <StepCard icon="/img/business/icon_telecom.svg" name="이동통신사" />
                  <Connector />
                  <StepCard icon="/img/business/icon_phone.svg" name="통신사 가입자" />
                </div>
              </div>
            </div>
          </section>

          {/* 서비스 정의 및 제공 방식 */}
          <section className="mb-[80px]">
            <div className="flex flex-col gap-[55px] mt-[50px]">
              <div>
                <Badge size="md" color="white" className="bg-[#1972D2]">
                  서비스 정의
                </Badge>
                <p className="text-[1.8rem] leading-[1.7] ml-[100px] mt-[20px]">
                  기업이 이통사 구분 없이 대량의 문자 메시지를 무선 단말기(휴대폰, PDA)로 전송할 수
                  있는 서비스입니다.
                </p>
              </div>
              <div>
                <Badge size="md" color="white" className="bg-[#1972D2]">
                  서비스 제공 방식
                </Badge>
                <ul className="list-disc my-[20px] pl-[20px] ml-[100px]">
                  <li className="text-[1.8rem] leading-[1.7] mb-[8px]">
                    <strong>기업형 SMS 서비스 :</strong> 통합 Java Agent 설치 후 DB Insert 방식으로
                    대량 전송을 처리하며, 높은 커스터마이징이 가능합니다.
                  </li>
                  <li className="text-[1.8rem] leading-[1.7] mb-[8px]">
                    <strong>기업형 엑셀 메신저 :</strong> 엑셀 포맷으로 데이터를 작성하여 일괄
                    전송(대량 전송 가능)합니다. 별도의 연동 작업 없이 즉시 이용 가능합니다.
                  </li>
                  <li className="text-[1.8rem] leading-[1.7] mb-[8px]">
                    <strong>프리미엄 서비스 :</strong> ASP 형태의 WEB LINK 방식으로, 고객사의
                    웹사이트와 연동하여 직접 정보 조회 서비스를 이용할 수 있습니다.
                  </li>
                </ul>
              </div>
            </div>

            {/* 전송 시스템의 성능 */}
            <div className="mt-[50px]">
              <Badge size="md" color="white" className="bg-[#1972D2]">
                전송 시스템의 성능
              </Badge>
              <div className="bg-[#f8f9fa] p-[40px_20px] rounded-lg text-center overflow-x-auto">
                <div className="flex items-center justify-center gap-[30px] max-w-[1000px] mx-auto ">
                  {/* Service Grid */}
                  <div className="grid grid-cols-2 gap-[15px] flex-shrink-0">
                    <StepCard icon="/img/business/icon_bank.svg" name="금융 서비스" small />
                    <StepCard icon="/img/business/icon_transit.svg" name="물류/유통" small />
                    <StepCard icon="/img/business/icon_institutions.svg" name="공공기관" small />
                    <StepCard
                      icon="/img/business/icon_internetShopping.svg"
                      name="인터넷 비즈니스"
                      small
                    />
                    <StepCard icon="/img/business/icon_news.svg" name="언론" small />
                    <StepCard icon="/img/business/icon_mans.svg" name="법인 등" small />
                  </div>

                  <FlowArrow />

                  {/* Center Hub */}
                  <div className="flex-shrink-0 text-center">
                    <div className="flex gap-[15px] mb-[20px] justify-center">
                      <StepCard
                        icon="/img/business/icon_database.svg"
                        name="SK브로드밴드<br/>IDC SMS Center"
                      />
                      <StepCard icon="/img/business/icon_database.svg" name="KT<br/>SMS Center" />
                    </div>
                    <div>
                      <strong className="block text-[2rem] text-gray-6 mt-[38px] mb-[8px]">
                        국내 최고의 SMS 전송 서비스 제공
                      </strong>
                      <p className="text-[2rem] text-gray-6 leading-[1.4]">
                        완벽한 이중화 시스템 지원
                        <br />
                        다양한 분야의 대량 메시지 전송
                      </p>
                    </div>
                  </div>

                  <FlowArrow />

                  {/* Usage List */}
                  <div className="text-left flex-shrink-0 min-w-[260px]">
                    <ul className="p-0 list-none">
                      {[
                        "카드 승인 내역 통보",
                        "입출금 거래 / 결제일 / 금액 통보",
                        "민원 접수 / 처리 과정 / 결과 통보",
                        "각종 안내 / 통지 서비스",
                        "이벤트 광고 / 각종 홍보",
                        "배송 현황 / 물류 추적",
                        "차량 현황 / 실시간 알람",
                        "상품 안내 / 홍보 / 경매 내역 통보",
                        "메시지 / 메일 도착 알람",
                        "각종 산업군 홍보 및 알람 용도",
                      ].map((item, idx) => (
                        <li
                          key={idx}
                          className="text-[1.8rem] text-gray-7 mb-[8px] relative pl-[15px] before:content-['•'] before:absolute before:left-0 before:text-gray-4"
                        >
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* SMS MO 서비스 */}
          <section className="mb-[80px]">
            <div className="mb-[50px]">
              <h2 className="text-[3.6rem] text-black font-bold mb-[25px] pb-[5px]">
                SMS MO 서비스
              </h2>
              <p className="text-[2rem]">
                사용자가 휴대폰에서 작성한 문자 메시지를 기업의 시스템(웹)으로 수신하는 양방향
                커뮤니케이션 서비스입니다.
              </p>
            </div>

            <div className="flex flex-col gap-[55px] mt-[50px]">
              <div>
                <Badge size="md" color="white" className="bg-[#1972D2]">
                  서비스 정의
                </Badge>

                <p className="text-[1.8rem] leading-[1.7] ml-[100px] mt-[20px]">
                  휴대폰에서 작성된 문자 메시지(SMS)를 웹 기반 시스템으로 전송하는 양방향
                  서비스입니다.
                </p>
              </div>
              <div>
                <Badge size="md" color="white" className="bg-[#1972D2]">
                  서비스 기대 효과
                </Badge>
                <ol className="list-decimal my-[20px] pl-[20px] ml-[100px]">
                  <li className="text-[1.8rem] leading-[1.7] mb-[8px]">
                    <strong>수익원 확보 :</strong> 서비스 이용료 및 이벤트 참여 수익 등 확실한
                    고수익 사업 모델 확보
                  </li>
                  <li className="text-[1.8rem] leading-[1.7] mb-[8px]">
                    <strong>마케팅 효율 :</strong> 전체 휴대폰 사용자에 대한 광범위한 서비스 제공 및
                    타겟 마케팅 활용
                  </li>
                  <li className="text-[1.8rem] leading-[1.7] mb-[8px]">
                    <strong>데이터 확보 :</strong> 서비스 이용자 DB 확보를 통한 CRM 데이터 축적 및
                    활용
                  </li>
                  <li className="text-[1.8rem] leading-[1.7] mb-[8px]">
                    <strong>사업 영역 확대 :</strong> 고객 정보 기반의 새로운 사업 영역 및 서비스
                    확대 기회 마련
                  </li>
                </ol>
              </div>
            </div>
          </section>
        </div>

        {/* MMS Tab Content */}
        <div className={activeTab === "mms" ? "block" : "hidden"} role="tabpanel">
          {/* MMS MT 서비스 */}
          <section className="mb-[80px]">
            <div className="mb-[50px]">
              <h2 className="text-[3.6rem] text-black font-bold mb-[25px] pb-[5px]">
                MMS MT 서비스
              </h2>
              <p className="text-[2rem]">
                'Multi-media Messaging Service'의 약자로 이동통신 메시징 서비스로 단말기로 촬영한
                사진 및 동영상과
                <br />
                장문의 텍스트를 첨부하여 보낼 수 있는 확장된 개념의 무선 메시징 서비스입니다.
              </p>
            </div>

            <div className="flex flex-col gap-[55px] mt-[50px]">
              <div>
                <h3 className="inline-block text-[2rem] text-black font-medium border-b border-black mt-[28px] mb-[18px]">
                  서비스 특징
                </h3>
                <ul className="list-disc my-[20px] pl-[20px] ml-[100px]">
                  <li className="text-[1.8rem] leading-[1.7] mb-[8px]">
                    <strong>통신사 연동 :</strong> 국내 3개 이동통신사와 유일하게 MMS를 연동하여
                    폭넓은 서비스 범위를 확보합니다.
                  </li>
                  <li className="text-[1.8rem] leading-[1.7] mb-[8px]">
                    <strong>전송 인프라 :</strong> KIDC와 하나로 IDC의 양방향 전용망을 통해 국내
                    최대 전송량 및 방대한 자료 전송이 가능하며, 철저한 모니터링으로 최상의 서비스를
                    제공합니다.
                  </li>
                  <li className="text-[1.8rem] leading-[1.7] mb-[8px]">
                    <strong>서비스 확장성 :</strong> 단순 MMS 기능에 머무르지 않고, ARS, MO, SMS,
                    E-mail 등 다양한 상품과 연계하여 창의성과 편의성을 극대화한 서비스를 제공합니다.
                  </li>
                  <li className="text-[1.8rem] leading-[1.7] mb-[8px]">
                    <strong>적용 모델 :</strong> MMS형 이메일, 포인트 쇼핑, CRM 쿠폰, MMS 전단 등
                    다양한 마케팅 모델에 즉시 적용 가능합니다.
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="inline-block text-[2rem] text-black font-medium border-b border-black mt-[28px] mb-[18px]">
                  서비스 제공 방식
                </h3>
                <div className="text-center">
                  {/* MO Flow Map */}
                  <div className="flex items-center justify-start gap-[30px] my-[50px] ml-[100px]">
                    <StepCard icon="/img/business/icon_customer.svg" name="고객사" />
                    <div className="text-navy">
                      <span className="material-icons-round text-[6rem]">east</span>
                    </div>
                    <StepCard icon="/img/business/icon_database.svg" name="자사 MO 플랫폼" />
                  </div>

                  <ul className="list-disc my-[20px] pl-[20px] text-left ml-[100px]">
                    <li className="text-[1.8rem] leading-[1.7] mb-[8px]">
                      <strong>이벤트 공지 -</strong> Multi Media 이벤트 공지. GIF 등 동적 이미지를
                      이용한 이벤트 공지로 참여도 증가
                    </li>
                    <li className="text-[1.8rem] leading-[1.7] mb-[8px]">
                      <strong>각종 상세 정보 -</strong> 80Bytes 이상 정보 전달. 제품정보/홍보/개인
                      통지 등을 80Bytes의 제한 없이 자유롭게 전달 가능
                    </li>
                    <li className="text-[1.8rem] leading-[1.7] mb-[8px]">
                      <strong>모바일 문서 -</strong> 무선 전자 문서 서비스. 처방전, 보험 증권 등을
                      모바일로 전송
                    </li>
                    <li className="text-[1.8rem] leading-[1.7] mb-[8px]">
                      <strong>브랜드 PR -</strong> 인지도 재고를 위한 Multi Media PR 각종 멀티미디어
                      메시징 서비스를 통한 효과적 정보 전달
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* MMS MO 서비스 */}
          <section className="mb-[80px]">
            <div className="mb-[50px]">
              <h2 className="text-[3.6rem] text-black font-bold mb-[25px] pb-[5px]">
                MMS MO 서비스
              </h2>
              <p className="text-[2rem]">
                'Mobile Originated Service'의 약자로 MMS 기능을 이용하여 TV, 라디오, 커뮤니티/블로그
                등의 서비스와 접목시켜
                <br />
                휴대폰에서 서버로의 이미지 데이터와 장문의 텍스트를 업로드 하는 서비스입니다.
              </p>
            </div>

            <div className="flex flex-col gap-[55px] mt-[50px]">
              <div>
                <h3 className="inline-block text-[2rem] text-black font-medium border-b border-black mt-[28px] mb-[18px]">
                  MMS MO 서비스 프로세스
                </h3>
                {/* MMS MO Flow */}
                <div className="flex items-center justify-center py-[90px] gap-[15px] ">
                  {/* Category Grid */}
                  <div className="grid grid-cols-2 gap-[10px]">
                    <StepCard icon="/img/business/icon_bang.svg" name="사건" small />
                    <StepCard icon="/img/business/icon_life.svg" name="생활정보" small />
                    <StepCard icon="/img/business/icon_news.svg" name="특종" small />
                    <StepCard icon="/img/business/icon_pr.svg" name="개인PR" small />
                    <StepCard icon="/img/business/icon_event.svg" name="이벤트" small />
                  </div>

                  <FlowArrow />

                  <StepCard
                    icon="/img/business/icon_phoneMsg.svg"
                    name="이미지 및<br/>텍스트 작성"
                  />

                  <FlowArrow label={`MMS MO\n특번 전송`} />

                  <StepCard icon="/img/business/icon_database.svg" name="자사 MO 플랫폼" />

                  <FlowArrow label="TCP/IP" />

                  <StepCard icon="/img/business/icon_customer.svg" name="고객사" />
                </div>
              </div>

              <div>
                <h3 className="inline-block text-[2rem] text-black font-medium border-b border-black mt-[28px] mb-[18px]">
                  기대 효과
                </h3>
                <ol className="list-decimal my-[20px] pl-[20px] ml-[100px]">
                  <li className="text-[1.8rem] leading-[1.7] mb-[8px]">
                    <strong>업무 유연성 확보 :</strong> 언제 어디서나 거리나 장소의 제약 없이 업무
                    처리가 가능하며, 이동 중에도 각종 정보 이용 및 전자 메일 송수신이 가능합니다.
                  </li>
                  <li className="text-[1.8rem] leading-[1.7] mb-[8px]">
                    <strong>새로운 업무 모델 실현 :</strong> Mobile Office 실현에 가장 적합한
                    시스템으로, 재택 근무, 시간 자유재 근무, 현장 업무 마감 등 신개념의 업무 모델을
                    실현합니다.
                  </li>
                  <li className="text-[1.8rem] leading-[1.7] mb-[8px]">
                    <strong>기업 활동 극대화 :</strong> One-Stop 시스템 적용으로 기업 활동의 효율을
                    극대화하고, 21세기형 비즈니스 모델 적용을 통해 대 고객 신뢰도를 향상시킵니다.
                  </li>
                </ol>
              </div>
            </div>
          </section>
        </div>

        {/* SFA Tab Content */}
        <div className={activeTab === "sfa" ? "block" : "hidden"} role="tabpanel">
          <section className="mb-[80px]">
            <div className="mb-[50px]">
              <h2 className="text-[3.6rem] text-black font-bold mb-[25px] pb-[5px]">
                SFA FFA 서비스
              </h2>
              <p className="text-[2rem]">
                영업(Sales) 및 현장 작업(Field) 과정의 자동화와 고객 관계 관리(CRM)를 통해 고객 유지
                향상 및 영업 활동을 지원하는 모바일 솔루션입니다.
                <br />
                고객 정보, 물품 정보 등 사내의 각종 D/B 정보를 실시간으로 제공 받아 업무 시간의
                단축과 영업력의 증대, 영업권 및 업무 영역 확장이 가능해집니다.
              </p>
            </div>

            {/* SFA Service Container */}
            <div className="flex items-center justify-center py-[50px] gap-[20px] ">
              {/* Left Side Group */}
              <div className="flex flex-col gap-[20px]">
                <StepCard icon="/img/business/icon_customer.svg" name="고객사" />
                <StepCard icon="/img/business/icon_file.svg" name="Back Office" />
              </div>

              <div className="flex flex-col items-center gap-[8px]">
                <span className="text-[14px] font-semibold text-gray-7">인터넷</span>
                <img
                  src="/img/business/SyncAlt.svg"
                  alt=""
                  className="w-[45px] h-auto opacity-70"
                />
              </div>

              <StepCard icon="/img/business/icon_database.svg" name="기업 IT 인프라" />

              <Connector />

              <StepCard icon="/img/business/icon_customer.svg" name="현장" />
            </div>

            <div>
              <h3 className="inline-block text-[2rem] text-black font-medium border-b border-black mt-[28px] mb-[18px]">
                기대 효과
              </h3>
              <ol className="list-decimal my-[20px] pl-[20px] ml-[100px]">
                <li className="text-[1.8rem] leading-[1.7] mb-[8px]">
                  MMS 메시징을 이용한 서비스 방식의 무선채널로의 확장
                </li>
                <li className="text-[1.8rem] leading-[1.7] mb-[8px]">
                  서비스의 쌍방향성과 실시간성을 보장
                </li>
                <li className="text-[1.8rem] leading-[1.7] mb-[8px]">
                  부가 정보 이용료 적용을 통한 수익모델 접근이 용이
                </li>
                <li className="text-[1.8rem] leading-[1.7] mb-[8px]">
                  현장에서 실시간 사진 전송을 통하여 현장 제보 등의 서비스 접목 가능
                </li>
                <li className="text-[1.8rem] leading-[1.7] mb-[8px]">
                  현재 미니홈피/블로그등의 1인 미디어와의 결합 서비스로 사용자에게 잘 인식되어 있음
                </li>
              </ol>
            </div>
          </section>
        </div>
        {/* CTA */}
        <section className="text-center ">
          <h3 className="text-[2.8rem] font-bold mb-4">
            더 빠르고 손쉬운
            <br />
            문자 발송을 위해
          </h3>
          <Link
            to="/service/submit"
            className="inline-flex items-center gap-2 mt-6 px-8 py-4 bg-navy text-white rounded-sm text-[1.6rem] font-medium hover:bg-navy/90 transition-colors"
          >
            서비스 신청하기
          </Link>
        </section>
      </section>
    </Container>
  );
};

export default BusinessMessaging;
